import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Search, Filter, Check, X, RotateCcw, CheckCircle } from "lucide-react";

export default function AdminAppointments() {
  const { token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: "",
    barber_id: "",
    date: ""
  });

  const fetchAppointments = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append("status", filters.status);
      if (filters.barber_id) params.append("barber_id", filters.barber_id);
      if (filters.date) params.append("date", filters.date);
      
      const response = await axios.get(`${API}/appointments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAppointments(response.data);
    } catch (error) {
      toast.error("Randevular yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchBarbers = async () => {
      try {
        const response = await axios.get(`${API}/barbers`);
        setBarbers(response.data);
      } catch (error) {
        console.error("Failed to fetch barbers");
      }
    };
    fetchBarbers();
  }, []);

  useEffect(() => {
    fetchAppointments();
  }, [token, filters]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(
        `${API}/appointments/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Randevu ${status === "approved" ? "onaylandı" : status === "rejected" ? "reddedildi" : status === "completed" ? "tamamlandı" : "güncellendi"}`);
      fetchAppointments();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { class: "status-pending", text: "Onay Bekliyor" },
      approved: { class: "status-approved", text: "Onaylandı" },
      rejected: { class: "status-rejected", text: "Reddedildi" },
      completed: { class: "status-completed", text: "Tamamlandı" },
      cancelled: { class: "status-cancelled", text: "İptal Edildi" }
    };
    const statusInfo = statusMap[status] || { class: "", text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const clearFilters = () => {
    setFilters({ status: "", barber_id: "", date: "" });
  };

  return (
    <AdminLayout title="Randevu Yönetimi">
      <div data-testid="admin-appointments">
        {/* Filters */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Select value={filters.status} onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}>
                <SelectTrigger className="bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e]">
                  <SelectValue placeholder="Durum Filtrele" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#e5e7eb]">
                  <SelectItem value="pending">Onay Bekliyor</SelectItem>
                  <SelectItem value="approved">Onaylandı</SelectItem>
                  <SelectItem value="completed">Tamamlandı</SelectItem>
                  <SelectItem value="rejected">Reddedildi</SelectItem>
                  <SelectItem value="cancelled">İptal Edildi</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <Select value={filters.barber_id} onValueChange={(value) => setFilters(prev => ({ ...prev, barber_id: value }))}>
                <SelectTrigger className="bg-[#fafafa] border-[#e5e7eb] text-[#1a1a2e]">
                  <SelectValue placeholder="Berber Filtrele" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#e5e7eb]">
                  {barbers.map((barber) => (
                    <SelectItem key={barber.id} value={barber.id}>{barber.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <Input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters(prev => ({ ...prev, date: e.target.value }))}
                className="input-elite"
                data-testid="date-filter"
              />
            </div>

            <Button onClick={clearFilters} variant="ghost" className="text-[#6b7280]">
              <RotateCcw className="w-4 h-4 mr-2" />
              Temizle
            </Button>
          </div>
        </div>

        {/* Appointments Table */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-x-auto shadow-sm">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12 text-[#6b7280]">
              Randevu bulunamadı
            </div>
          ) : (
            <table className="table-elite">
              <thead>
                <tr>
                  <th>Müşteri</th>
                  <th>Hizmet</th>
                  <th>Berber</th>
                  <th>Tarih</th>
                  <th>Saat</th>
                  <th>Durum</th>
                  <th>Tutar</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appointment) => (
                  <tr key={appointment.id} data-testid={`appointment-row-${appointment.id}`}>
                    <td>
                      <div>
                        <p className="text-[#1a1a2e]">{appointment.customer_name || "—"}</p>
                        <p className="text-[#9ca3af] text-sm">{appointment.customer_phone}</p>
                      </div>
                    </td>
                    <td className="text-[#1a1a2e]">{appointment.service_name}</td>
                    <td className="text-[#1a1a2e]">{appointment.barber_name}</td>
                    <td className="text-[#1a1a2e]">
                      {format(new Date(appointment.date), "d MMM yyyy", { locale: tr })}
                    </td>
                    <td className="text-[#1a1a2e]">{appointment.time}</td>
                    <td>{getStatusBadge(appointment.status)}</td>
                    <td className="text-[#c9a96e] font-accent">{appointment.service_price}₺</td>
                    <td>
                      <div className="flex gap-2">
                        {appointment.status === "pending" && (
                          <>
                            <button
                              onClick={() => updateStatus(appointment.id, "approved")}
                              className="p-2 bg-emerald-600/20 text-emerald-600 hover:bg-emerald-600/30 rounded-lg transition-colors"
                              title="Onayla"
                              data-testid={`approve-${appointment.id}`}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => updateStatus(appointment.id, "rejected")}
                              className="p-2 bg-red-600/20 text-red-600 hover:bg-red-600/30 rounded-lg transition-colors"
                              title="Reddet"
                              data-testid={`reject-${appointment.id}`}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {appointment.status === "approved" && (
                          <button
                            onClick={() => updateStatus(appointment.id, "completed")}
                            className="p-2 bg-blue-600/20 text-blue-600 hover:bg-blue-600/30 rounded-lg transition-colors"
                            title="Tamamla"
                            data-testid={`complete-${appointment.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
